/**
 * An invited administrator gives their name and chooses a password (D-62).
 *
 * The link is `/accept-invite#<token>`, kept out of logs and history as the
 * reset link is (`PasswordReset.tsx`). Nothing happens on arrival, so a mail
 * scanner that follows the link spends nothing. The password is asked twice
 * and compared here; the server checks the length again and the link always.
 */

import { type FormEvent, useId, useState } from "react";
import { failure, MIN_LENGTH, takeToken } from "@/auth/PasswordReset";
import { BrandMark } from "@/components/ui/BrandMark";
import { Button, FieldShell, TextInput } from "@/components/ui/controls";
import { Notice } from "@/components/ui/states";
import { api } from "@/lib/api";

export function AcceptInvitePage() {
  const nameId = useId();
  const passwordId = useId();
  const repeatId = useId();
  const [token] = useState(takeToken);
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [repeat, setRepeat] = useState("");
  const [problem, setProblem] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const [pending, setPending] = useState(false);

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    if (!name.trim()) {
      setProblem("Enter your name.");
      return;
    }
    if (password.length < MIN_LENGTH) {
      setProblem(`Use at least ${MIN_LENGTH} characters.`);
      return;
    }
    if (password !== repeat) {
      setProblem("The two passwords are not the same.");
      return;
    }
    setProblem(null);
    setPending(true);
    try {
      await api.acceptInvite(token, name.trim(), password);
      setDone(true);
    } catch (cause) {
      setProblem(failure(cause));
    } finally {
      setPending(false);
    }
  }

  return (
    <main className="grid min-h-dvh place-items-center px-4 py-12">
      <div className="w-full max-w-sm">
        <header className="mb-6">
          <p className="mb-5 flex items-center gap-2">
            <BrandMark size="size-7" />
            <span className="label-tiny">Future Minds CMS</span>
          </p>
          <h1 className="text-display font-semibold tracking-tight text-ink">
            {done ? "Your account is ready" : "Set up your account"}
          </h1>
          <p className="mt-0.5 text-md text-ink-muted">
            {done
              ? "Sign in with your email address and the password you just chose."
              : "You have been invited as an administrator. Choose a password you do not use anywhere else."}
          </p>
        </header>

        {done ? (
          <a
            href="/"
            className="panel block rounded-panel p-5 text-center font-medium text-accent"
          >
            Sign in
          </a>
        ) : !token ? (
          <Notice tone="danger">
            This page needs the link from your invitation email. Ask an administrator to invite
            you again.
          </Notice>
        ) : (
          <form onSubmit={onSubmit} noValidate className="panel grid gap-4 rounded-panel p-5">
            {problem && <Notice tone="danger">{problem}</Notice>}
            <FieldShell label="Your name" htmlFor={nameId} required>
              <TextInput
                id={nameId}
                autoComplete="name"
                autoFocus
                required
                maxLength={200}
                value={name}
                onChange={(event) => setName(event.target.value)}
              />
            </FieldShell>
            <FieldShell
              label="Password"
              htmlFor={passwordId}
              required
              help={`At least ${MIN_LENGTH} characters.`}
            >
              <TextInput
                id={passwordId}
                type="password"
                autoComplete="new-password"
                required
                value={password}
                onChange={(event) => setPassword(event.target.value)}
              />
            </FieldShell>
            <FieldShell label="The same again" htmlFor={repeatId} required>
              <TextInput
                id={repeatId}
                type="password"
                autoComplete="new-password"
                required
                value={repeat}
                onChange={(event) => setRepeat(event.target.value)}
              />
            </FieldShell>
            <Button type="submit" variant="primary" pending={pending} pendingLabel="Saving…">
              Create my account
            </Button>
          </form>
        )}
      </div>
    </main>
  );
}
